import { useState } from 'react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuSeparator
} from './ui/dropdown-menu';
import { Button } from './ui/button';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import {
  DollarSign, Heart, Lock, Rocket, Calendar, CreditCard
} from 'lucide-react';
import TipJarModal from './TipJarModal';
import { BoostPostModal } from './BoostPostModal';

export default function MonetizeMenu({ post, isOwner }) {
  const { isAuthenticated } = useAuth();
  const [tipOpen, setTipOpen] = useState(false);
  const [boostOpen, setBoostOpen] = useState(false);

  const creatorName = post.author_name || post.artist_name || 'Creator';
  const postId = post.post_id;

  const handleAction = (action) => {
    if (!isAuthenticated) {
      toast.info('Sign in to use monetization features');
      return;
    }

    switch (action) {
      case 'tip':
        setTipOpen(true);
        break;
      case 'boost':
        setBoostOpen(true);
        break;
      case 'lock':
        toast.info('Use the lock icon when creating a post to set pay-to-unlock');
        break;
      case 'booking':
        // Navigate to the creator's booking page
        const handle = post.author_handle || post.handle;
        if (handle) {
          window.location.href = `/u/${handle}?tab=about`;
        } else {
          toast.info('Booking is available on the creator\'s profile');
        }
        break;
      default:
        break;
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="gap-1.5 hover:bg-emerald-500/10 hover:text-emerald-400 text-muted-foreground/70"
            data-testid={`monetize-btn-${postId}`}
          >
            <DollarSign className="w-4 h-4" />
            <span className="hidden sm:inline text-xs">Monetize</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-52" data-testid="monetize-menu">
          {/* Fans see tip option */}
          {!isOwner && (
            <DropdownMenuItem
              onClick={() => handleAction('tip')}
              className="gap-2 cursor-pointer"
              data-testid="monetize-tip"
            >
              <Heart className="w-4 h-4 text-pink-500" />
              <div>
                <p className="font-medium text-sm">Send Tip</p>
                <p className="text-[10px] text-muted-foreground">Support this creator</p>
              </div>
            </DropdownMenuItem>
          )}

          {/* Owner sees manage options */}
          {isOwner && (
            <>
              <DropdownMenuItem
                onClick={() => handleAction('boost')}
                className="gap-2 cursor-pointer"
                data-testid="monetize-boost"
              >
                <Rocket className="w-4 h-4 text-orange-500" />
                <div>
                  <p className="font-medium text-sm">Boost Post</p>
                  <p className="text-[10px] text-muted-foreground">Promote to more people</p>
                </div>
              </DropdownMenuItem>

              <DropdownMenuItem
                onClick={() => handleAction('lock')}
                className="gap-2 cursor-pointer"
                data-testid="monetize-lock"
              >
                <Lock className="w-4 h-4 text-amber-500" />
                <div>
                  <p className="font-medium text-sm">Pay to Unlock</p>
                  <p className="text-[10px] text-muted-foreground">Set a price for this content</p>
                </div>
              </DropdownMenuItem>
            </>
          )}

          <DropdownMenuSeparator />

          <DropdownMenuItem
            onClick={() => handleAction('booking')}
            className="gap-2 cursor-pointer"
            data-testid="monetize-booking"
          >
            <Calendar className="w-4 h-4 text-cyan-500" />
            <div>
              <p className="font-medium text-sm">Book / Hire</p>
              <p className="text-[10px] text-muted-foreground">Studio, features, shoots</p>
            </div>
          </DropdownMenuItem>

          {!isOwner && (
            <DropdownMenuItem
              onClick={() => {
                const handle = post.author_handle || post.handle;
                if (handle) window.location.href = `/u/${handle}?tab=about`;
                else toast.info('Visit the creator\'s profile to subscribe');
              }}
              className="gap-2 cursor-pointer"
              data-testid="monetize-subscribe"
            >
              <CreditCard className="w-4 h-4 text-violet-500" />
              <div>
                <p className="font-medium text-sm">Subscribe</p>
                <p className="text-[10px] text-muted-foreground">Monthly support tiers</p>
              </div>
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Modals */}
      <TipJarModal
        open={tipOpen}
        onOpenChange={setTipOpen}
        postId={postId}
        creatorName={creatorName}
      />
      <BoostPostModal
        open={boostOpen}
        onOpenChange={setBoostOpen}
        postId={postId}
      />
    </>
  );
}
